import { h } from './dom.js';

export interface ComputerControlState {
  enabled: boolean;
  accessibility: boolean;
  screenPermission: string;
  active: boolean;
  actions: Array<{ id: string; action: string; label: string; status: string }>;
  message?: string;
}

export interface ComputerControlRequest {
  actor: 'user' | 'device';
  ttlMs: number;
  id: string;
  clientId: string;
  action: string;
  args: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ComputerControlBridge {
  status(): Promise<ComputerControlState>;
  setEnabled(enabled: boolean): Promise<ComputerControlState>;
  stop(): Promise<ComputerControlState>;
  openPermission(permission: 'accessibility' | 'screen'): Promise<void>;
  execute(request: ComputerControlRequest): Promise<Record<string, unknown>>;
  onState(listener: (state: ComputerControlState) => void): () => void;
}

const ACTION_STATUS: Record<string, string> = {
  pending: 'Pendiente', queued: 'Pendiente', running: 'En curso',
  confirming: 'Espera tu confirmación', confirmation_required: 'Espera tu confirmación',
  succeeded: 'Hecha', completed: 'Hecha', done: 'Hecha', ok: 'Hecha',
  failed: 'No se pudo completar', error: 'No se pudo completar',
  cancelled: 'Detenida', canceled: 'Detenida', denied: 'No autorizada', blocked: 'Bloqueada',
};

/** Only the local bridge can authorize execution. The renderer relays requests/results. */
export function mountComputerControl(options: {
  host: HTMLElement;
  desktop?: ComputerControlBridge;
  api: { computerActionResult(id: string, result: Record<string, unknown>): Promise<unknown> };
}): { destroy(): void; handle(request: ComputerControlRequest): Promise<void> } {
  const { host, desktop, api } = options;
  if (!desktop) return { destroy() {}, async handle() {} };
  let destroyed = false;
  let revision = 0;
  let state: ComputerControlState | undefined;
  let changing = false;
  const input = h('input', { type: 'checkbox', role: 'switch', disabled: true, 'aria-label': 'Controlar este Mac' });
  const status = h('p', { class: 'computer-control-note', role: 'status', 'aria-live': 'polite' }, ['Consultando este Mac…']);
  const error = h('p', { class: 'computer-control-error', role: 'alert', hidden: true });
  const accessibility = h('span', {}, ['Accesibilidad: sin comprobar']);
  const screen = h('span', {}, ['Grabación de pantalla: sin comprobar']);
  const controls = h('details', { class: 'computer-control-details' }, [
    h('summary', {}, ['Permisos y privacidad']),
    h('p', { class: 'computer-control-note' }, ['Las capturas se envían al modelo configurado para interpretarlas. No se guardan en el historial.']),
  ]);
  function showError(message: string): void {
    if (destroyed) return;
    error.textContent = message;
    error.hidden = false;
  }
  for (const [permission, label, info] of [
    ['accessibility', 'Abrir Accesibilidad', accessibility],
    ['screen', 'Abrir Grabación de pantalla', screen],
  ] as const) {
    const button = h('button', { type: 'button', class: 'btn ghost' }, [label]);
    button.addEventListener('click', () => {
      void desktop!.openPermission(permission).catch(() => showError('No se pudo abrir el panel de permisos. Ábrelo desde los ajustes de privacidad de macOS.'));
    });
    controls.append(h('div', { class: 'computer-control-permission' }, [info, button]));
  }
  const panel = h('section', { class: 'computer-control', 'aria-label': 'Control de este Mac' }, [
    h('label', { class: 'computer-control-toggle' }, [input, h('span', {}, ['Controlar este Mac'])]),
    status, controls, error,
  ]);
  const stop = h('button', { type: 'button', class: 'btn danger' }, ['Detener']);
  const actions = h('ol', { class: 'computer-control-actions', 'aria-label': 'Acciones de esta orden' });
  const banner = h('section', { class: 'computer-control-banner', 'aria-label': 'Control del Mac en curso', hidden: true }, [
    h('div', { class: 'computer-control-banner-heading' }, [
      h('strong', { role: 'status', 'aria-live': 'polite' }, ['ASIS está controlando tu Mac']), stop,
    ]), actions,
  ]);
  host.append(panel);
  document.body.append(banner);

  function render(next: ComputerControlState): void {
    if (destroyed) return;
    revision++;
    state = next;
    input.checked = next.enabled;
    input.disabled = changing;
    accessibility.textContent = `Accesibilidad: ${next.accessibility ? 'permitida' : 'necesita permiso'}`;
    screen.textContent = `Grabación de pantalla: ${next.screenPermission === 'granted' ? 'permitida' : next.screenPermission === 'denied' ? 'denegada' : next.screenPermission === 'restricted' ? 'restringida' : 'necesita permiso'}`;
    status.textContent = next.message || (next.enabled ? 'Activado en este Mac. Puedes detener el control en cualquier momento.' : '');
    status.hidden = !status.textContent;
    controls.hidden = !next.enabled;
    banner.hidden = !next.active;
    actions.replaceChildren(...next.actions.slice(0, 15).map(action => h('li', {}, [
      h('span', {}, [action.label]),
      h('span', { class: 'computer-control-action-status' }, [ACTION_STATUS[action.status] ?? 'En curso']),
    ])));
  }

  async function refresh(): Promise<void> {
    const before = revision;
    try { const next = await desktop!.status(); if (before === revision) render(next); }
    catch { showError('No se pudo consultar el control de este Mac. Vuelve a la ventana para reintentarlo.'); }
  }
  input.addEventListener('change', () => {
    if (changing || !state) return;
    const wanted = input.checked;
    input.checked = state.enabled;
    input.disabled = true;
    changing = true;
    error.hidden = true;
    void desktop!.setEnabled(wanted).then(render).catch(() => {
      showError('No se pudo cambiar el permiso. El control conserva su estado anterior.');
    }).finally(() => { changing = false; if (!destroyed) input.disabled = !state; });
  });
  stop.addEventListener('click', () => {
    stop.disabled = true;
    void desktop!.stop().then(render).catch(() => showError('No se pudo confirmar la parada. Desactiva «Controlar este Mac» o cierra ASIS.'))
      .finally(() => { if (!destroyed) stop.disabled = false; });
  });
  const unsubscribe = desktop.onState(render);
  window.addEventListener('focus', refresh);
  void refresh();
  return {
    destroy() {
      destroyed = true;
      unsubscribe();
      window.removeEventListener('focus', refresh);
      panel.remove();
      banner.remove();
    },
    async handle(request) {
      if (destroyed) return;
      let result: Record<string, unknown>;
      try { result = await desktop.execute(request); }
      catch { result = { ok: false, error: 'failed' }; }
      // Image bytes pass directly to the result endpoint; never render or persist them.
      if (destroyed) return;
      try { await api.computerActionResult(request.id, result.ok === true ? {ok:true,data:result.data,clientId:request.clientId} : {ok:false,error:result.error,clientId:request.clientId}); }
      catch { showError('No se pudo comunicar el resultado de la acción. No se repetirá automáticamente.'); void desktop.stop(); }
      finally { if(result.data&&typeof result.data==='object')delete (result.data as Record<string,unknown>).imageBase64; }
    },
  };
}
